import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, INTUITION, TRIGGER, CODE, APPLY, HONESTY, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/** Small honesty pill — on-brand "not-yet-shipped" tag. */
const Tag: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <span
    style={{
      fontFamily: FONTS.mono,
      fontSize: 16,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color,
      border: `1px solid ${color}88`,
      borderRadius: 6,
      padding: "4px 10px",
    }}
  >
    {text}
  </span>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const on = interpolate(frame, [42, 72], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="self-learning agents · what shipped" color={COLORS.green} />
          <Big>
            my AI{" "}
            <span style={{ color: COLORS.green, opacity: 0.35 + on * 0.65 }}>
              writes its own skills
            </span>
            <br />
            after every chat
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="green" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the amnesia tax" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="cyan" title="this session" width={460}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div style={{ color: COLORS.dim }}>you: “always cite the file”</div>
              <div style={{ color: COLORS.dim }}>you: “stop guessing names”</div>
              <div style={{ color: COLORS.green }}>agent: got it ✓</div>
            </div>
          </Panel>
          <Panel accent="red" title="next session" width={460}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.red }}>blank slate</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>the correction evaporated</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="amber" />
  </AbsoluteFill>
);

/* 03 — INTUITION */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 34, alignItems: "center" }}>
          <Big size={62}>each turn</Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</div>
          <Big size={62}>“a rule?”</Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</div>
          <Big size={62}>
            <span style={{ color: COLORS.green }}>write it down</span>
          </Big>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 30 }}>
          a cheap reviewer watches the conversation, not the human
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — TRIGGER (pipeline) */
const PIPE: PipeNode[] = [
  { label: "chat turn", value: "ASSISTANT", sub: "reply sent", accent: "cyan" },
  { label: "fire-and-forget", value: "void review", sub: "no latency hit", accent: "green" },
  { label: "reviewer", value: "Gemini Flash", sub: "last 6 turns", accent: "violet" },
  { label: "output", value: "0–3 RULES", sub: "persona-rule", accent: "lime" },
];
export const Scene04Trigger: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.trigger.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="route.ts · backgroundReviewTurn()" color={COLORS.green} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={24} nodeWidth={230} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, marginTop: 34 }}>
        runs after every assistant turn · errors swallowed · chat never blocks
      </div>
    </AbsoluteFill>
    <Caption lines={TRIGGER} accent="green" />
  </AbsoluteFill>
);

/* 05 — CODE */
const TS = `// src/lib/skills/sources/background-review.ts
// per-turn proposer — fired fire-and-forget from the chat route
const system = \`You are EAKC's skill-review reviewer.
Identify if THIS turn would make a useful PERSONA-RULE.

CRITICAL — DO NOT INVENT signals. No rule unless you can
quote a SPECIFIC user sentence that motivates it.
Default to an empty array: false positives pollute
the agent's persona.\`;

// guards before any skill is created:
if (looksLikeEcho(p)) continue;          // drop echoed examples
if (await isDuplicate(slug)) continue;   // 7-day dedup window
if ((await isThrashing(slug)).thrashing) // already-rejected?
  continue;

const skill = await createSkill({ kind: 'persona-rule',
  state: 'proposed', body: p.body,
  attachedTo: [{ kind: 'agent', id: agentId }] });`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={TS}
        language="tsx"
        filename="background-review.ts"
        highlightLines={[5, 6, 14, 15, 16, 17]}
        revealFrames={320}
        fontSize={22}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 06 — APPLY + UNDO */
const PERSONA = `persistent_agents.persona_prompt

  …your existing persona…

  <!-- SKILL:8f2c… -->
  Always cite the exact file path when
  referencing code, e.g. src/lib/x.ts.
  <!-- /SKILL:8f2c… -->`;
export const Scene06Apply: React.FC = () => {
  const frame = useCurrentFrame();
  const undo = interpolate(frame, [300, 330], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.apply.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
        <Kicker text="auto-apply · with deterministic undo" color={COLORS.green} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <CodePanel
            code={PERSONA}
            language="tsx"
            filename="persona-rule handler → writes a MARKED block"
            highlightLines={[5, 6, 7, 8]}
            revealFrames={150}
            fontSize={21}
            width={920}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 18, justifyContent: "center", width: 420 }}>
            <Callout label="apply" accent="green" width={420}>
              body wrapped in <b style={{ color: COLORS.text }}>{"<!-- SKILL:id -->"}</b> markers, appended to the persona
            </Callout>
            <div style={{ opacity: undo }}>
              <Callout label="undo · the safety net" accent="cyan" width={420}>
                strip the marked block → persona <b style={{ color: COLORS.text }}>byte-for-byte restored</b>
              </Callout>
            </div>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={APPLY} accent="green" />
    </AbsoluteFill>
  );
};

/* 07 — HONESTY (the partial-ship tag) */
export const Scene07Honesty: React.FC = () => {
  const frame = useCurrentFrame();
  const c2 = interpolate(frame, [150, 180], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.honesty.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the honest scoreboard" color={COLORS.amber} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 22 }}>
          <MetricCounter value={10} startFrame={10} durationFrames={50} accent="green" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.text }}>auto-apply</span>
          <span style={{ fontFamily: FONTS.display, fontSize: 50, color: COLORS.dim }}>·</span>
          <MetricCounter value={2} startFrame={26} durationFrames={50} accent="amber" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.text }}>gated</span>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 14 }}>
          12 skill kinds · recipients + tool grants always need approval
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 34, opacity: c2 }}>
          <Tag text="auto-apply + undo = shipped" color={COLORS.green} />
          <Tag text="auto-promotion = queued" color={COLORS.amber} />
        </div>
      </AbsoluteFill>
      <Caption lines={HONESTY} accent="amber" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          Learn from the chat.
          <br />
          <span style={{ color: COLORS.green }}>Auto-apply.</span>{" "}
          <span style={{ color: COLORS.cyan }}>Always undoable.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · self-improving agents
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="green" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
